/**
 * Minimal IndexedDB wrapper for the durability layer.
 * Two stores:
 *   - drafts: keyed by `${userId}:${formKey}` → { value, updated_at }
 *   - queue:  auto-increment id → { url, method, headers, body, isMultipart, files, userId, attempts, lastError, nextRetryAt, created_at }
 */

const DB_NAME = 'spliteasy-durability';
const DB_VERSION = 1;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('drafts')) {
        db.createObjectStore('drafts', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('queue')) {
        const store = db.createObjectStore('queue', { keyPath: 'id', autoIncrement: true });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('nextRetryAt', 'nextRetryAt', { unique: false });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => {
      // Reset cache so future calls can retry rather than being stuck rejected
      dbPromise = null;
      reject(e.target.error);
    };
  });
  return dbPromise;
}

function tx(storeName, mode, fn) {
  return openDB().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let result;
    try {
      result = fn(store, t);
    } catch (err) {
      reject(err);
      return;
    }
    t.oncomplete = () => resolve(result instanceof IDBRequest ? result.result : result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

/* ---------- Drafts API ---------- */

export async function saveDraft(userId, formKey, value) {
  if (!userId || !formKey) return;
  const id = `${userId}:${formKey}`;
  return tx('drafts', 'readwrite', (store) => {
    store.put({ id, value, updated_at: Date.now() });
  });
}

export async function loadDraft(userId, formKey) {
  if (!userId || !formKey) return null;
  const id = `${userId}:${formKey}`;
  return tx('drafts', 'readonly', (store) => {
    return new Promise((resolve) => {
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  });
}

export async function clearDraft(userId, formKey) {
  if (!userId || !formKey) return;
  const id = `${userId}:${formKey}`;
  return tx('drafts', 'readwrite', (store) => {
    store.delete(id);
  });
}

export async function listDrafts(userId) {
  return tx('drafts', 'readonly', (store) => {
    return new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const all = req.result || [];
        resolve(userId ? all.filter((d) => d.id.startsWith(`${userId}:`)) : all);
      };
      req.onerror = () => resolve([]);
    });
  });
}

/* ---------- Queue API ---------- */

export async function enqueue(item) {
  return tx('queue', 'readwrite', (store) => {
    return new Promise((resolve) => {
      const req = store.add({
        ...item,
        attempts: item.attempts || 0,
        lastError: item.lastError || null,
        nextRetryAt: item.nextRetryAt || Date.now(),
        created_at: item.created_at || Date.now(),
      });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
  });
}

export async function listQueue(userId) {
  return tx('queue', 'readonly', (store) => {
    return new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const all = req.result || [];
        resolve(userId ? all.filter((q) => q.userId === userId) : all);
      };
      req.onerror = () => resolve([]);
    });
  });
}

export async function updateQueueItem(id, patch) {
  return tx('queue', 'readwrite', (store) => {
    return new Promise((resolve) => {
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const cur = getReq.result;
        if (!cur) { resolve(null); return; }
        const updated = { ...cur, ...patch };
        const putReq = store.put(updated);
        putReq.onsuccess = () => resolve(updated);
        putReq.onerror = () => resolve(null);
      };
      getReq.onerror = () => resolve(null);
    });
  });
}

export async function deleteQueueItem(id) {
  return tx('queue', 'readwrite', (store) => {
    store.delete(id);
  });
}

export async function clearQueueForUser(userId) {
  const items = await listQueue(userId);
  await Promise.all(items.map((i) => deleteQueueItem(i.id)));
}

/* ---------- Persistent storage (anti-eviction) ---------- */

export async function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      const granted = await navigator.storage.persist();
      return !!granted;
    }
  } catch {
    /* ignore */
  }
  return false;
}
